import React from 'react'
import { startCase } from 'lodash'
import {
  Button,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Select,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { FormControl } from '@/features/forms/components'

import { UserFormInput } from '../types'
import { CityCollection } from '@/features/types'
import { useCityOptions } from '@/lib/hooks/useCityOptions'
import { OCCUPATIONS } from '../constants'
import { formatDateTime, t } from '@/lib/helpers'

type Props = {
  defaultValues: UserFormInput
  onSubmitForm: (data: UserFormInput) => void
  cities: CityCollection
  isLoading: boolean
}

export const UserForm = (props: Props) => {
  const { defaultValues = {}, onSubmitForm, cities, isLoading } = props

  const {
    register,
    handleSubmit,
    watch,
    errors,
    formState: { isSubmitting },
  } = useForm<UserFormInput>({
    defaultValues,
    resolver: yupResolver(schema),
  })

  const onSubmit = (data: UserFormInput) => {
    if (isSubmitting) return

    onSubmitForm(data)
  }

  const cityOptions = useCityOptions(cities, { emptyFirstItem: true })

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid sx={{ gap: '24px 16px' }}>
        <FormControl id="nickname" label={t('views.admin.users.form.nickname')}>
          <Input
            size="lg"
            placeholder={t('views.admin.users.form.nickname')}
            ref={register}
            name="nickname"
            tabIndex={1}
          />
        </FormControl>

        <FormControl
          id="email"
          label={t('views.admin.users.form.email')}
          errorMsg={errors.email?.message}
          isRequired
        >
          <Input
            type="email"
            size="lg"
            placeholder="example@example.com"
            ref={register}
            name="email"
            tabIndex={2}
          />
        </FormControl>

        <FormControl
          id="occupation"
          label={t('views.admin.users.form.occupation')}
          errorMsg={errors.occupation?.message}
        >
          <Select
            size="lg"
            ref={register}
            name="occupation"
            tabIndex={3}
            sx={{ fontSize: '16px' }}
          >
            {OCCUPATIONS.map(({ label, value }) => (
              <option key={value} value={value}>
                {startCase(label)}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl
          id="face_icon"
          label={t('views.admin.users.form.face_icon')}
        >
          <Input
            name="face_icon"
            ref={register}
            tabIndex={4}
            size="lg"
            disabled
          />
        </FormControl>

        <FormControl
          id="birthdate"
          label={t('views.admin.users.form.birthdate')}
          errorMsg={errors.birthdate?.message}
        >
          <Input
            type="date"
            name="birthdate"
            ref={register}
            tabIndex={4}
            size="lg"
          />
        </FormControl>

        <FormControl
          id="city_id"
          label={t('views.admin.users.form.city')}
          errorMsg={errors.city_id?.message}
        >
          <Select
            size="lg"
            ref={register}
            name="city_id"
            tabIndex={5}
            sx={{ fontSize: '16px' }}
          >
            {cityOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl
          id="toshi_os_has_connected"
          label={t('views.admin.users.form.toshi_os_has_connected')}
          errorMsg={errors.toshi_os_has_connected?.message}
        >
          <Input
            name="toshi_os_has_connected"
            ref={register}
            tabIndex={4}
            size="lg"
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_unlinked_by_error_at"
          label={t('views.admin.users.form.toshi_os_unlinked_by_error_at')}
        >
          <Input
            name="toshi_os_unlinked_by_error_at"
            ref={register}
            tabIndex={4}
            size="lg"
            value={formatDateTime(watch('toshi_os_unlinked_by_error_at')) || ''}
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_has_agreed"
          label={t('views.admin.users.form.toshi_os_has_agreed')}
          errorMsg={errors.toshi_os_has_agreed?.message}
        >
          <Input
            name="toshi_os_has_agreed"
            ref={register}
            tabIndex={4}
            size="lg"
            value={watch('toshi_os_has_agreed') ? t('views.admin.users.form.value_true') : t('views.admin.users.form.valur_false')}
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_has_enabled_point_service"
          label={t('views.admin.users.form.toshi_os_has_enabled_point_service')}
          errorMsg={errors.toshi_os_has_enabled_point_service?.message}
        >
          <Input
            name="toshi_os_has_enabled_point_service"
            ref={register}
            tabIndex={4}
            size="lg"
            value={watch('toshi_os_has_enabled_point_service') ? t('views.admin.users.form.value_true') : t('views.admin.users.form.valur_false')}
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_points"
          label={t('views.admin.users.form.toshi_os_points')}
          errorMsg={errors.toshi_os_points?.message}
        >
          <Input
            name="toshi_os_points"
            ref={register}
            tabIndex={4}
            size="lg"
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_last_fetched_at"
          label={t('views.admin.users.form.toshi_os_last_fetched_at')}
          errorMsg={errors.toshi_os_last_fetched_at?.message}
        >
          <Input
            name="toshi_os_last_fetched_at"
            ref={register}
            tabIndex={4}
            size="lg"
            value={formatDateTime(watch('toshi_os_last_fetched_at')) || ''}
            disabled
          />
        </FormControl>

        <FormControl
          id="toshi_os_user_id"
          label={t('views.admin.users.form.toshi_os_user_id')}
        >
          <Input
            name="toshi_os_user_id"
            ref={register}
            tabIndex={4}
            size="lg"
            value={watch('toshi_os_user_id') || ''}
            disabled
          />
        </FormControl>
      </Grid>

      <Button
        type="submit"
        sx={{ w: '100%', mt: '32px', h: '54px' }}
        isLoading={isSubmitting || isLoading}
        tabIndex={6}
      >
        {t('views.admin.users.form.update')}
      </Button>
    </form>
  )
}

// Mirrors the API's own Devise `config.email_regexp`. Deliberately NOT yup's
// `.email()`, whose regex demands a dotted domain: it would reject addresses the
// API has already accepted, and react-hook-form validates the WHOLE schema on
// submit, so an untouched address would block edits to every other field.
const EMAIL_REGEXP = /^[^@\s]+@[^@\s]+$/

const schema = yup.object().shape({
  nickname: yup.string(),
  email: yup
    .string()
    .required(t('views.admin.shared.form.required'))
    .matches(EMAIL_REGEXP, t('views.admin.shared.form.invalid_email')),
  point: yup.number().integer(),
  occupation: yup.string().oneOf(OCCUPATIONS.map((o) => o.value)),
  birthdate: yup.string(),
  city_id: yup.number().integer(),
})
